import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, input, output } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormsModule } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatDialog } from "@angular/material/dialog";
import { MatMenu, MatMenuItem, MatMenuTrigger } from "@angular/material/menu";
import { RouterLink } from "@angular/router";
import { Observable, catchError, of, switchMap } from "rxjs";
import { map } from "rxjs/operators";
import { BadgeComponent } from "@sxm-content-creators/tools-ds";
import { AuthService } from "../../../auth/auth.service";
import { DateRangeBarComponent } from "../../../components/date-range-bar/date-range-bar.component";
import { ConfirmModalComponent, ConfirmModalData } from "../../../components/confirm-modal/confirm-modal.component";
import { SnackbarService } from "../../../components/snackbar/snackbar.service";
import { InteractiveDisplayState, getInteractiveDisplayState } from "../../models/interactive.model";
import { InteractiveListItem } from "../../models/interactives-list.model";
import {
    InteractiveMenuAction,
    InteractiveMenuItem,
    MENU_ACTIONS_BY_STATE,
    MENU_ACTION_LABELS,
    STATE_CHANGE_COPY,
    StateChangeAction,
    stateChangeConfirmData
} from "../../models/interactives-list.view";
import { INTERACTIVE_PATCH_BUILDERS, toInteractiveTypedId } from "../../mappers/interactives-list.mapper";
import { INTERACTIVE_DISPLAY_STATE_BADGE_LIBRARY } from "../../pipes/interactive-display-state.pipe";
import { RelativeTimePipe } from "../../pipes/relative-time.pipe";
import { TagListComponent } from "../../../components/tag/tag-list.component";
import { FreeformApiService } from "../../services/freeform.api.service";
import { PollApiService } from "../../services/poll.api.service";

@Component({
    selector: "app-interactive-list-result",
    templateUrl: "./interactive-list-result.component.html",
    styleUrl: "./interactive-list-result.component.scss",
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    host: { role: "listitem" },
    imports: [
        BadgeComponent,
        DateRangeBarComponent,
        RelativeTimePipe,
        MatButtonModule,
        MatMenu,
        MatMenuItem,
        MatMenuTrigger,
        RouterLink,
        TagListComponent,
        FormsModule
    ]
})
export class InteractiveListResultComponent {
    private readonly freeformApi = inject(FreeformApiService);
    private readonly pollApi = inject(PollApiService);
    private readonly dialog = inject(MatDialog);
    private readonly snackbar = inject(SnackbarService);
    private readonly destroyRef = inject(DestroyRef);
    private readonly auth = inject(AuthService);

    readonly item = input.required<InteractiveListItem>();

    /** Emitted after a state change succeeds so the list can update this row in place. */
    readonly stateChanged = output<StateChangeAction>();

    protected readonly badgeLibrary = INTERACTIVE_DISPLAY_STATE_BADGE_LIBRARY;

    protected readonly isMarketer = computed(() => this.auth.isMarketer());

    protected readonly imageSrc = computed(() => this.item().imageUrl || "assets/tenant-no-image.png");

    protected readonly producerLink = computed(() => {
        return ["producer", toInteractiveTypedId(this.item())];
    });

    protected readonly displayState = computed(() => getInteractiveDisplayState(this.item()));

    protected readonly badgeVariant = computed(() => this.badgeLibrary[this.displayState()]);

    protected readonly types = computed(() => {
        const item = this.item();
        const types: { label: string; icon: string }[] = [];
        if (item.isAudio) types.push({ label: "Voice", icon: "icon-iec-microphone" });
        if (item.isText) types.push({ label: "Text", icon: "icon-iec-text-msg" });
        if (item.isPoll) types.push({ label: "Poll", icon: "icon-iec-graph" });
        if (item.isVideo) types.push({ label: "Video", icon: "icon-iec-video-camera" });
        return types;
    });

    protected readonly startDate = computed(() => toDate(this.item().startDate));
    protected readonly endDate = computed(() => toDate(this.item().endDate));

    protected readonly topTags = computed(() => {
        const item = this.item();
        const tags = item.isPoll ? item.pollStats?.topTags : item.freeformStats?.topTags;
        return tags ?? [];
    });

    protected readonly topTagOptions = computed(() => this.topTags().map(tag => ({ value: tag, label: tag })));

    protected readonly topSelectionLines = computed<string[]>(() => {
        const stats = this.item().pollStats;
        if (!stats) return ["—"];

        if (stats.topOptionsCount > 1) {
            const votes = stats.topOptionsVotes === 1 ? "1 vote" : `${stats.topOptionsVotes} votes`;
            return [`${stats.topOptionsCount} selections •`, `${votes} each`];
        }

        return [stats.topSelection ?? "—"];
    });

    /**
     * Display state to drive the row menu. A PAUSED interactive whose end date has passed can no
     * longer be resumed, so its menu should match ARCHIVED.
     */
    protected readonly menuState = computed<InteractiveDisplayState>(() => {
        const state = this.displayState();
        const end = this.endDate();
        if (state === "PAUSED" && end !== null && end.getTime() < Date.now()) return "ARCHIVED";
        return state;
    });

    readonly menuItems = computed<InteractiveMenuItem[]>(() => {
        const item = this.item();
        return MENU_ACTIONS_BY_STATE[this.menuState()].map(action => ({
            action,
            label: MENU_ACTION_LABELS[action],
            link: this.linkFor(action, item)
        }));
    });

    onAction(action: InteractiveMenuAction): void {
        if (action === "edit" || action === "duplicate") return; // handled via routerLink

        const item = this.item();
        this.dialog
            .open<ConfirmModalComponent, ConfirmModalData, boolean>(ConfirmModalComponent, {
                data: stateChangeConfirmData(action, item)
            })
            .afterClosed()
            .pipe(
                switchMap(confirmed => (confirmed ? this.runAction(action, item) : of(false))),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(success => {
                if (success) this.stateChanged.emit(action);
            });
    }

    private runAction(action: StateChangeAction, item: InteractiveListItem): Observable<boolean> {
        const request: Observable<unknown> =
            action === "delete" ? this.deleteInteractive(item) : this.patchInteractive(action, item);
        return request.pipe(
            map(() => {
                this.snackbar.show(STATE_CHANGE_COPY[action].success, "success");
                return true;
            }),
            catchError(() => {
                this.snackbar.show(STATE_CHANGE_COPY[action].error, "error");
                return of(false);
            })
        );
    }

    private deleteInteractive(item: InteractiveListItem): Observable<unknown> {
        return item.isPoll ? this.pollApi.delete(item.id) : this.freeformApi.delete(item.id);
    }

    private patchInteractive(
        action: Exclude<StateChangeAction, "delete">,
        item: InteractiveListItem
    ): Observable<unknown> {
        const patch = INTERACTIVE_PATCH_BUILDERS[action](item);
        return item.isPoll ? this.pollApi.update(item.id, patch) : this.freeformApi.update(item.id, patch);
    }

    private linkFor(action: InteractiveMenuAction, item: InteractiveListItem): string[] | undefined {
        if (action === "edit") return ["/interactives", toInteractiveTypedId(item), "edit"];
        if (action === "duplicate") return ["/interactives", toInteractiveTypedId(item), "duplicate"];
        return undefined;
    }
}

function toDate(value: string | null): Date | null {
    return value ? new Date(value) : null;
}
